/**
 * API client for communicating with the PriceSpy Worker
 */

import { getAccessToken } from './auth-store'

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8790'

export interface ApiError {
  error: string
}

// Auth types
export interface LoginRequest {
  email: string
  password: string
}

export interface SignupRequest {
  email: string
  password: string
  name?: string
}

export interface AuthResponse {
  accessToken: string
  refreshToken: string
  expiresIn: number
  user: {
    id: string
    email: string
    name: string | null
  }
}

export interface PasswordResetRequest {
  email: string
}

export interface ResetPasswordRequest {
  token: string
  password: string
}

export interface VerifyEmailRequest {
  token: string
}

// Tracked URL types
export type TrackedUrlStatus = 'active' | 'paused' | 'error'
export type CheckFrequency = 'hourly' | 'six_hour' | 'daily'

export interface TrackedUrl {
  id: string
  url: string
  displayName: string | null
  status: TrackedUrlStatus
  checkFrequency: CheckFrequency
  requiresBrowser: boolean
  lastCheckedAt: string | null
  nextCheckAt: string | null
  lastChangeAt: string | null
  lastError: string | null
  errorCount: number
  createdAt: string
}

export interface CreateTrackedUrlRequest {
  url: string
  displayName?: string
}

export interface UpdateTrackedUrlRequest {
  displayName?: string
  status?: TrackedUrlStatus
}

// Snapshot types
export interface Snapshot {
  id: string
  trackedUrlId: string
  contentHash: string
  extractedData: Record<string, unknown>
  createdAt: string
}

// Alert types
export type AlertChannel = 'email' | 'slack' | 'webhook'

export interface AlertConfig {
  channelType: AlertChannel
  enabled: boolean
  config: Record<string, unknown>
  updatedAt: string
}

// Change types
export type ChangeType =
  | 'price_increase'
  | 'price_decrease'
  | 'new_plan'
  | 'removed_plan'
  | 'feature_added'
  | 'feature_removed'
  | 'plan_renamed'
  | 'currency_change'
  | 'major_restructure'

// Dashboard types
export interface DashboardStats {
  totalUrls: number
  activeUrls: number
  pausedUrls: number
  errorUrls: number
  changesLast7Days: number
  alertsSentLast7Days: number
  planLimit: number
  planName: string
}

// Settings types
export interface UserProfile {
  id: string
  email: string
  name: string | null
  timezone: string | null
  plan: string
  emailVerified: boolean
  createdAt: string
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    requireAuth = false
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    if (requireAuth) {
      const token = getAccessToken()
      if (token) {
        ;(headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
      }
    }

    let response: Response
    try {
      response = await fetch(url, {
        ...options,
        headers,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      if (message.includes('fetch') || message.includes('network') || message.includes('Failed')) {
        throw new Error(
          `Unable to connect to the API server at ${this.baseUrl}. ` +
            'Please check that the server is running and accessible.'
        )
      }
      throw new Error(`Network error: ${message}`)
    }

    let data: unknown
    try {
      data = await response.json()
    } catch {
      if (!response.ok) {
        throw new Error(
          `Server error (${response.status}): ${response.statusText || 'Unknown error'}`
        )
      }
      throw new Error('Invalid response from server')
    }

    if (!response.ok) {
      const errorData = data as ApiError
      const errorMessage = errorData.error ?? `Request failed with status ${response.status}`
      throw new Error(errorMessage)
    }

    return data as T
  }

  // Auth

  async login(credentials: LoginRequest): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    })
  }

  async signup(
    data: SignupRequest
  ): Promise<{ user: { id: string; email: string }; message: string }> {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async refreshToken(
    refreshToken: string
  ): Promise<{ accessToken: string; refreshToken: string; expiresIn: number }> {
    return this.request('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    })
  }

  async requestPasswordReset(data: PasswordResetRequest): Promise<{ message: string }> {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async resetPassword(data: ResetPasswordRequest): Promise<{ message: string }> {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async verifyEmail(data: VerifyEmailRequest): Promise<{ message: string }> {
    return this.request('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // Tracked URLs

  async listTrackedUrls(params?: {
    page?: number
    limit?: number
    status?: TrackedUrlStatus
    search?: string
  }): Promise<PaginatedResponse<TrackedUrl>> {
    const query = new URLSearchParams()
    if (params?.page !== undefined) query.set('page', params.page.toString())
    if (params?.limit !== undefined) query.set('limit', params.limit.toString())
    if (params?.status) query.set('status', params.status)
    if (params?.search) query.set('search', params.search)
    const qs = query.toString()
    return this.request(qs ? `/urls?${qs}` : '/urls', {}, true)
  }

  async getTrackedUrl(id: string): Promise<TrackedUrl> {
    return this.request(`/urls/${id}`, {}, true)
  }

  async createTrackedUrl(data: CreateTrackedUrlRequest): Promise<TrackedUrl> {
    return this.request('/urls', { method: 'POST', body: JSON.stringify(data) }, true)
  }

  async updateTrackedUrl(id: string, data: UpdateTrackedUrlRequest): Promise<TrackedUrl> {
    return this.request(`/urls/${id}`, { method: 'PATCH', body: JSON.stringify(data) }, true)
  }

  async deleteTrackedUrl(id: string): Promise<{ message: string }> {
    return this.request(`/urls/${id}`, { method: 'DELETE' }, true)
  }

  async refreshTrackedUrl(id: string): Promise<{ message: string }> {
    return this.request(`/urls/${id}/refresh`, { method: 'POST' }, true)
  }

  // Snapshots

  async listSnapshots(
    urlId: string,
    params?: { page?: number; limit?: number }
  ): Promise<PaginatedResponse<Snapshot>> {
    const query = new URLSearchParams()
    if (params?.page !== undefined) query.set('page', params.page.toString())
    if (params?.limit !== undefined) query.set('limit', params.limit.toString())
    const qs = query.toString()
    return this.request(
      qs ? `/urls/${urlId}/snapshots?${qs}` : `/urls/${urlId}/snapshots`,
      {},
      true
    )
  }

  async getSnapshot(id: string): Promise<Snapshot> {
    return this.request(`/snapshots/${id}`, {}, true)
  }

  // Alerts

  async listAlertConfigs(): Promise<{ data: AlertConfig[] }> {
    return this.request('/alert-configs', {}, true)
  }

  async upsertAlertConfig(
    channel: AlertChannel,
    data: { enabled: boolean; config: Record<string, unknown> }
  ): Promise<AlertConfig> {
    return this.request(
      `/alert-configs/${channel}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
      true
    )
  }

  async deleteAlertConfig(channel: AlertChannel): Promise<{ message: string }> {
    return this.request(`/alert-configs/${channel}`, { method: 'DELETE' }, true)
  }

  async testAlertConfig(channel: AlertChannel): Promise<{ message: string }> {
    return this.request(`/alert-configs/${channel}/test`, { method: 'POST' }, true)
  }

  // Dashboard

  async getDashboardStats(): Promise<DashboardStats> {
    return this.request('/dashboard/stats', {}, true)
  }

  async getRecentChanges(): Promise<{ data: unknown[] }> {
    return this.request('/dashboard/recent-changes', {}, true)
  }

  // Settings

  async getProfile(): Promise<UserProfile> {
    return this.request('/settings/profile', {}, true)
  }

  async updateProfile(data: { name?: string; timezone?: string }): Promise<UserProfile> {
    return this.request(
      '/settings/profile',
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
      true
    )
  }

  async changePassword(data: {
    currentPassword: string
    newPassword: string
  }): Promise<{ message: string }> {
    return this.request(
      '/settings/change-password',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      true
    )
  }

  async deleteAccount(): Promise<{ message: string }> {
    return this.request('/settings/account', { method: 'DELETE' }, true)
  }

  async createApiKey(): Promise<{ apiKey: string; keyPrefix: string; message: string }> {
    return this.request('/settings/api-keys', { method: 'POST' }, true)
  }

  async revokeApiKey(id: string): Promise<{ message: string }> {
    return this.request(`/settings/api-keys/${id}`, { method: 'DELETE' }, true)
  }
}

export const api = new ApiClient(API_BASE_URL)
